/**
 * /rc-calculator — постоянная времени, заряд конденсатора и RC-фильтр.
 *
 * Почему отдельный файл, а не инлайн в блейде, как было: логика перестала быть
 * «умножить R на C». Здесь живут разбор сокращённой записи номиналов (4u7),
 * ряды E12/E24/E96, обратная задача «нужно 20 мс — из чего собрать», разброс от
 * допуска номиналов и три вида графика с общим маркером. Инлайновый скрипт на
 * 600 строк не кэшируется и не версионируется отдельно от страницы.
 *
 * Единицы и фразы приходят из блейда (window.__rccU / window.__rccL) — вторая
 * копия подписи в JS разъезжается с переводом молча.
 *
 * ⚠️ Числа форматируются через Intl: в русской локали разделитель — запятая, и
 * «0.159 Hz» на месте «0,159 Гц» было настоящим дефектом старой версии.
 */
(function () {
    'use strict';

    var app = document.getElementById('rccApp');
    if (!app) return;

    var U = window.__rccU || {};
    var L = window.__rccL || {};
    var LOC = window.__LOCALE || 'en';

    var $ = function (id) { return document.getElementById(id); };

    /* ═══════════════════════════════════════════════════════════════════════
       1. ЧИСЛА И ЕДИНИЦЫ
       ═══════════════════════════════════════════════════════════════════════ */

    /** Значащие цифры, а не знаки после запятой: 0,159 и 159 одинаково читаемы,
        а «0.16» и «159.00» — нет. */
    function nf(v, sig) {
        if (!isFinite(v)) return '—';
        sig = sig || 3;
        try {
            return new Intl.NumberFormat(LOC, { maximumSignificantDigits: sig }).format(v);
        } catch (_) {
            return String(Number(v.toPrecision(sig)));
        }
    }
    /** Фиксированные знаки — для процентов и децибел, где шкала своя.
        ⚠️ Ноль приводится к нулю без знака: у фильтра на декаду выше среза
        ослабление ФВЧ равно −0,04 дБ, и «−0,0 дБ» на экране читается как
        опечатка, а не как «практически ничего». */
    function nfd(v, d) {
        if (!isFinite(v)) return '—';
        var r = +v.toFixed(d);
        if (r === 0) r = 0;
        try {
            return new Intl.NumberFormat(LOC, { minimumFractionDigits: d, maximumFractionDigits: d }).format(r);
        } catch (_) { return r.toFixed(d); }
    }
    /** Градусы пишутся вплотную к числу — это знак, а не единица. */
    function fmtDeg(v) { return nfd(v, 0) + U.deg; }
    function fmtDb(v) { return nfd(v, 1) + ' ' + U.db; }

    /* Лесенки «значение → единица». Каждая описана ОДИН раз и работает в обе
       стороны: и на печать, и на разбор ввода, и на выбор единицы в селекторе. */
    var LADDER = {
        time:  [[60, U.min], [1, U.s], [1e-3, U.ms], [1e-6, U.us], [1e-9, U.ns]],
        freq:  [[1e6, U.mhz], [1e3, U.khz], [1, U.hz]],
        volt:  [[1, U.v], [1e-3, U.mv]],
        amp:   [[1, U.a], [1e-3, U.ma], [1e-6, U.ua], [1e-9, U.na]],
        joule: [[1, U.j], [1e-3, U.mj], [1e-6, U.uj], [1e-9, U.nj]],
        ohm:   [[1e6, U.mohm], [1e3, U.kohm], [1, U.ohm]],
        farad: [[1e-3, U.mf], [1e-6, U.uf], [1e-9, U.nf], [1e-12, U.pf]]
    };

    /** Формат по лесенке. Микроскопический запас (1e-9) удерживает 10 кОм ×
        100 мкФ от чтения «1000 мс»: произведение float садится на 0,99999… —
        волосок ниже чистой границы единицы. */
    function byLadder(v, kind, sig) {
        if (!isFinite(v) || v <= 0) return '—';
        var l = LADDER[kind], e = 1 - 1e-9, i;
        for (i = 0; i < l.length; i++) {
            if (v >= l[i][0] * e) return nf(v / l[i][0], sig) + ' ' + l[i][1];
        }
        var last = l[l.length - 1];
        return nf(v / last[0], sig) + ' ' + last[1];
    }
    var fmtT = function (v, s) { return byLadder(v, 'time', s); };
    var fmtF = function (v, s) { return byLadder(v, 'freq', s); };
    var fmtV = function (v, s) { return byLadder(v, 'volt', s); };
    var fmtA = function (v, s) { return byLadder(v, 'amp', s); };
    var fmtJ = function (v, s) { return byLadder(v, 'joule', s); };
    var fmtR = function (v, s) { return byLadder(v, 'ohm', s); };
    var fmtC = function (v, s) { return byLadder(v, 'farad', s); };

    /* ═══════════════════════════════════════════════════════════════════════
       2. СОКРАЩЁННАЯ ЗАПИСЬ НОМИНАЛОВ
       Инженер пишет «4u7», а не «4,7» плюс поиск нужной строки в селекторе.
       Эта запись — стандарт маркировки (IEC 60062), а не жаргон: буква стоит
       на месте запятой, поэтому её нельзя потерять при печати.
       ═══════════════════════════════════════════════════════════════════════ */

    var SUF_R = {
        'r': 1, 'e': 1, 'ω': 1, 'Ω': 1, 'ohm': 1, 'ом': 1,
        'k': 1e3, 'к': 1e3, 'ком': 1e3, 'kohm': 1e3, 'ko': 1e3,
        'm': 1e6, 'м': 1e6, 'мом': 1e6, 'meg': 1e6, 'mo': 1e6
    };
    var SUF_C = {
        'p': 1e-12, 'п': 1e-12, 'pf': 1e-12, 'пф': 1e-12,
        'n': 1e-9, 'н': 1e-9, 'nf': 1e-9, 'нф': 1e-9,
        'u': 1e-6, 'µ': 1e-6, 'μ': 1e-6, 'mk': 1e-6, 'мк': 1e-6, 'uf': 1e-6, 'мкф': 1e-6,
        'm': 1e-3, 'мф': 1e-3, 'mf': 1e-3,
        'f': 1, 'ф': 1
    };

    /**
     * Возвращает { n, factor } либо null.
     * `factor === null` — человек ввёл голое число, единицу берём из селектора.
     * Понимает «4.7», «4,7», «4u7», «10k», «470R», «1M5», «100n», «22 пФ».
     */
    function parseValue(raw, sufMap) {
        var s = String(raw == null ? '' : raw).trim()
            .replace(/\s| | /g, '').replace(/,/g, '.');
        if (!s) return null;
        if (/^\d*\.?\d+$/.test(s)) return { n: parseFloat(s), factor: null };

        var m = s.match(/^(\d*\.?\d*)([^\d.]{1,4})(\d*)$/);
        if (!m) return null;
        var head = m[1], letters = m[2].toLowerCase(), tail = m[3];
        var f = sufMap[letters];
        if (f === undefined) return null;

        var num = head === '' ? '0' : head;
        if (tail !== '') num = (head === '' ? '0' : head) + '.' + tail;
        var n = parseFloat(num);
        if (!isFinite(n)) return null;
        return { n: n, factor: f };
    }

    /** Базовое значение → короткая запись для ссылки: 10000 → «10k». */
    function toShort(v, kind) {
        if (!isFinite(v) || v <= 0) return '';
        var tabs = kind === 'ohm'
            ? [[1e6, 'M'], [1e3, 'k'], [1, 'R']]
            : [[1e-3, 'm'], [1e-6, 'u'], [1e-9, 'n'], [1e-12, 'p']];
        var e = 1 - 1e-9, i;
        for (i = 0; i < tabs.length; i++) {
            if (v >= tabs[i][0] * e) return (+(v / tabs[i][0]).toPrecision(6)) + tabs[i][1];
        }
        var last = tabs[tabs.length - 1];
        return (+(v / last[0]).toPrecision(6)) + last[1];
    }

    /* ═══════════════════════════════════════════════════════════════════════
       3. РЯДЫ НОМИНАЛОВ
       ═══════════════════════════════════════════════════════════════════════ */

    var E12 = [10, 12, 15, 18, 22, 27, 33, 39, 47, 56, 68, 82];
    var E24 = [10, 11, 12, 13, 15, 16, 18, 20, 22, 24, 27, 30, 33, 36, 39, 43,
               47, 51, 56, 62, 68, 75, 82, 91];
    var E96 = [100, 102, 105, 107, 110, 113, 115, 118, 121, 124, 127, 130, 133, 137,
               140, 143, 147, 150, 154, 158, 162, 165, 169, 174, 178, 182, 187, 191,
               196, 200, 205, 210, 215, 221, 226, 232, 237, 243, 249, 255, 261, 267,
               274, 280, 287, 294, 301, 309, 316, 324, 332, 340, 348, 357, 365, 374,
               383, 392, 402, 412, 422, 432, 442, 453, 464, 475, 487, 499, 511, 523,
               536, 549, 562, 576, 590, 604, 619, 634, 649, 665, 681, 698, 715, 732,
               750, 768, 787, 806, 825, 845, 866, 887, 909, 931, 953, 976]
        .map(function (x) { return x / 10; });
    /* Конденсаторы в ящике идут по E6 — E24-ёмкости существуют в каталогах, но
       собрать из них макет нельзя, поэтому подбор предлагает то, что купишь. */
    var E6 = [10, 15, 22, 33, 47, 68];

    var SERIES = { E12: E12, E24: E24, E96: E96 };

    /** Ближайший номинал ряда к значению (в базовых единицах). */
    function snap(v, mant) {
        if (!(v > 0)) return v;
        var dec = Math.pow(10, Math.floor(Math.log10(v)) - 1);
        var best = null, bestErr = Infinity, i, k, cand;
        for (k = -1; k <= 1; k++) {
            for (i = 0; i < mant.length; i++) {
                cand = mant[i] * dec * Math.pow(10, k);
                var err = Math.abs(Math.log(cand / v));
                if (err < bestErr) { bestErr = err; best = cand; }
            }
        }
        return +best.toPrecision(6);
    }

    /** Шаг по ряду вверх/вниз — стрелками на клавиатуре прямо в поле. */
    function stepSeries(v, dir, mant) {
        if (!(v > 0)) return v;
        var dec = Math.pow(10, Math.floor(Math.log10(v)) - 1);
        var list = [], i, k;
        for (k = -1; k <= 1; k++) {
            for (i = 0; i < mant.length; i++) list.push(mant[i] * dec * Math.pow(10, k));
        }
        list.sort(function (a, b) { return a - b; });
        var eps = v * 1e-6;
        if (dir > 0) {
            for (i = 0; i < list.length; i++) if (list[i] > v + eps) return +list[i].toPrecision(6);
        } else {
            for (i = list.length - 1; i >= 0; i--) if (list[i] < v - eps) return +list[i].toPrecision(6);
        }
        return v;
    }

    /* ═══════════════════════════════════════════════════════════════════════
       4. СОСТОЯНИЕ
       ═══════════════════════════════════════════════════════════════════════ */

    var S = {
        R: 10000, C: 1e-4, V: 5,
        mode: 'charge',     // charge | discharge
        view: 'curve',      // curve | bode | phase
        target: 'tau',      // tau | fc
        series: 'E24',
        tolR: 5, tolC: 10,
        px: null            // положение маркера: секунды (curve) либо Гц (bode/phase)
    };

    /* ═══════════════════════════════════════════════════════════════════════
       5. ФИЗИКА
       ═══════════════════════════════════════════════════════════════════════ */

    function tau() { return S.R * S.C; }
    function fc() { return (S.R > 0 && S.C > 0) ? 1 / (2 * Math.PI * S.R * S.C) : 0; }
    /** Доля от конечного значения в момент t: заряд растёт, разряд убывает. */
    function levelAt(t) {
        var k = tau();
        if (!(k > 0)) return S.mode === 'charge' ? 0 : 1;
        var e = Math.exp(-t / k);
        return S.mode === 'charge' ? 1 - e : e;
    }
    /** Обратная задача по времени: до какого момента ждать уровня p (0…1). */
    function timeAtLevel(p) {
        var k = tau();
        if (!(k > 0)) return 0;
        if (S.mode === 'charge') {
            if (p <= 0) return 0;
            if (p >= 1) return Infinity;
            return -k * Math.log(1 - p);
        }
        if (p >= 1) return 0;
        if (p <= 0) return Infinity;
        return -k * Math.log(p);
    }
    /** Ток через резистор: и при заряде, и при разряде он спадает как e^(−t/τ). */
    function currentAt(t) {
        var k = tau();
        if (!(k > 0) || !(S.R > 0)) return 0;
        return (S.V / S.R) * Math.exp(-t / k);
    }
    function gainDb(f, hp) {
        var c = fc();
        if (!(c > 0) || !(f > 0)) return hp ? -Infinity : 0;
        var r = hp ? c / f : f / c;
        return -10 * Math.log10(1 + r * r);
    }
    function phaseDeg(f, hp) {
        var c = fc();
        if (!(c > 0) || !(f > 0)) return hp ? 90 : 0;
        return hp ? (90 - Math.atan(f / c) * 180 / Math.PI)
                  : (-Math.atan(f / c) * 180 / Math.PI);
    }
    /** По ослаблению ФНЧ обратно к частоте — вторая половина двусторонней строки. */
    function freqAtDb(db) {
        var c = fc();
        if (!(c > 0) || db >= 0) return c > 0 ? 0 : 0;
        var x = Math.pow(10, -db / 10) - 1;
        return x > 0 ? c * Math.sqrt(x) : 0;
    }
    function freqAtPhase(deg) {
        var c = fc();
        if (!(c > 0)) return 0;
        var d = Math.min(-0.01, Math.max(-89.99, deg));
        return c * Math.tan(-d * Math.PI / 180);
    }

    /** Границы допуска: «-20:80» для электролита — не симметрия, а паспорт. */
    function tolBounds(spec) {
        var s = String(spec);
        if (s.indexOf(':') > -1) {
            var p = s.split(':');
            return [parseFloat(p[0]) / 100, parseFloat(p[1]) / 100];
        }
        var v = parseFloat(s) / 100 || 0;
        return [-v, v];
    }

    /* ═══════════════════════════════════════════════════════════════════════
       6. ПОДБОР НОМИНАЛОВ (обратная задача)
       Человек знает, СКОЛЬКО должно длиться, и не знает, из чего это собрать.
       Ровно эту работу калькулятор τ = R·C за него не делает.
       ═══════════════════════════════════════════════════════════════════════ */

    var CAPS = (function () {
        var out = [], d, i;
        for (d = -12; d <= -2; d++) {
            for (i = 0; i < E6.length; i++) out.push(+(E6[i] / 10 * Math.pow(10, d)).toPrecision(4));
        }
        return out;
    })();

    function findFits(targetTau, seriesName) {
        if (!(targetTau > 0)) return [];
        var mant = SERIES[seriesName] || E24;
        var rows = [], i;
        for (i = 0; i < CAPS.length; i++) {
            var c = CAPS[i];
            var rWant = targetTau / c;
            if (rWant < 100 || rWant > 10e6) continue;       // вне того, что паяют
            var r = snap(rWant, mant);
            var got = r * c;
            var dev = (got - targetTau) / targetTau;
            /* Штраф за неудобный резистор. 220 Ω и 8,2 МΩ формально подходят,
               но у первого заметен ток покоя, а у второго на τ начинает влиять
               входное сопротивление того, что к цепи подключено. Поэтому вне
               килоомного диапазона — крупный штраф, а внутри — мягкое
               предпочтение середины: при равном отклонении первым должен
               стоять ответ, который человек и так бы выбрал. */
            var pen = (r >= 1e3 && r <= 1e6) ? 0 : 0.02;
            pen += 0.004 * Math.abs(Math.log10(r / 47e3));
            rows.push({ r: r, c: c, tau: got, dev: dev, score: Math.abs(dev) + pen });
        }
        rows.sort(function (a, b) { return a.score - b.score; });
        var seen = {}, out = [];
        for (i = 0; i < rows.length && out.length < 4; i++) {
            var key = rows[i].r + '/' + rows[i].c;
            if (seen[key]) continue;
            seen[key] = 1; out.push(rows[i]);
        }
        return out;
    }

    /* ═══════════════════════════════════════════════════════════════════════
       7. ХОЛСТ
       Три вида — кривая, АЧХ и фаза — это один и тот же прибор, поэтому у них
       общая геометрия, общий маркер и один проход отрисовки.
       ═══════════════════════════════════════════════════════════════════════ */

    var cv = $('rccCanvas');
    var ctx = cv.getContext('2d');
    var GEO = { w: 0, h: 0, padL: 46, padR: 12, padT: 12, padB: 24 };

    function cssv(name, fb) {
        var v = getComputedStyle(app).getPropertyValue(name).trim();
        return v || fb;
    }

    /** Пол по размеру обязателен: нулевой битмап рисует пустоту и не бросает
        ошибок — самый тихий способ показать человеку ничего. */
    function sizeCanvas() {
        var r = cv.getBoundingClientRect();
        var w = Math.max(200, Math.round(r.width || cv.clientWidth || 320));
        var h = Math.max(140, Math.round(r.height || cv.clientHeight || 180));
        var dpr = Math.min(window.devicePixelRatio || 1, 2.5);
        var bw = Math.max(1, Math.round(w * dpr)), bh = Math.max(1, Math.round(h * dpr));
        if (cv.width !== bw || cv.height !== bh) { cv.width = bw; cv.height = bh; }
        GEO.w = w; GEO.h = h;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        GEO.padL = w < 380 ? 38 : 46;
    }

    function X(fr) { return GEO.padL + fr * (GEO.w - GEO.padL - GEO.padR); }
    function Y(fr) { return GEO.padT + (1 - fr) * (GEO.h - GEO.padT - GEO.padB); }

    function line(x1, y1, x2, y2, color, width, dash) {
        ctx.save();
        ctx.strokeStyle = color; ctx.lineWidth = width || 1;
        if (dash) ctx.setLineDash(dash);
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
        ctx.restore();
    }
    function label(txt, x, y, color, align, size) {
        ctx.save();
        ctx.fillStyle = color;
        ctx.font = '600 ' + (size || 11) + 'px ' + cssv('--lg-font', 'system-ui, sans-serif');
        ctx.textAlign = align || 'start';
        ctx.textBaseline = 'middle';
        ctx.fillText(txt, x, y);
        ctx.restore();
    }
    /** Плашка маркера. roundRect есть везде с 2023-го, но падать из-за рамки
        нельзя — без неё рисуем прямоугольник. */
    function chip(txt, x, y, bg, fg) {
        ctx.save();
        ctx.font = '700 11px ' + cssv('--lg-font', 'system-ui, sans-serif');
        var w = ctx.measureText(txt).width + 14, h = 20;
        var cx = Math.min(Math.max(x - w / 2, GEO.padL), GEO.w - GEO.padR - w);
        var cy = Math.max(y - h - 8, 2);
        ctx.fillStyle = bg;
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(cx, cy, w, h, 6); else ctx.rect(cx, cy, w, h);
        ctx.fill();
        ctx.fillStyle = fg; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(txt, cx + w / 2, cy + h / 2 + .5);
        ctx.restore();
    }

    function palette() {
        return {
            grid: cssv('--lg-border', 'rgba(255,255,255,.10)'),
            gridHi: cssv('--lg-border-hi', 'rgba(255,255,255,.18)'),
            txt: cssv('--lg-text-3', '#8494a7'),
            txt2: cssv('--lg-text-2', '#94a3b8'),
            acc: cssv('--lg-accent', '#38bdf8'),
            warn: cssv('--lg-warn', '#fbbf24'),
            ink: cssv('--lg-text', '#f8fafc'),
            panel: cssv('--lg-surface-3', cssv('--lg-surface-2', '#151d32'))
        };
    }

    function draw() {
        sizeCanvas();
        ctx.clearRect(0, 0, GEO.w, GEO.h);
        var P = palette();
        if (S.view === 'curve') drawCurve(P); else drawBode(P, S.view === 'phase');
        // оси поверх сетки
        line(GEO.padL, GEO.padT, GEO.padL, Y(0), P.gridHi, 1);
        line(GEO.padL, Y(0), GEO.w - GEO.padR, Y(0), P.gridHi, 1);
    }

    /* ── кривая заряда/разряда ──────────────────────────────────────────── */
    function drawCurve(P) {
        var k = tau(), span = 5 * k, i;
        var isCh = S.mode === 'charge';

        for (i = 1; i <= 5; i++) {
            line(X(i / 5), GEO.padT, X(i / 5), Y(0), P.grid, 1, [3, 4]);
            label(i + 'τ', X(i / 5), Y(0) + 12, P.txt, 'center');
        }
        var marks = isCh ? [0, .5, .632, 1] : [0, .368, .5, 1];
        for (i = 0; i < marks.length; i++) {
            var m = marks[i];
            if (m > 0 && m < 1) line(GEO.padL, Y(m), GEO.w - GEO.padR, Y(m), P.grid, 1, [2, 5]);
            label(Math.round(m * 1000) / 10 + '%', GEO.padL - 6, Y(m), P.txt, 'end');
        }

        if (!(k > 0)) return;

        var pts = [], N = 160;
        for (i = 0; i <= N; i++) {
            var t = span * i / N;
            pts.push([X(i / N), Y(levelAt(t))]);
        }

        /* Заливка под кривой — «сколько уже набралось». Градиент идёт к той
           стороне, где идёт наполнение: при заряде вниз, при разряде вверх. */
        var g = ctx.createLinearGradient(0, GEO.padT, 0, Y(0));
        g.addColorStop(0, hexA(P.acc, isCh ? .02 : .40));
        g.addColorStop(1, hexA(P.acc, isCh ? .40 : .02));
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(pts[0][0], Y(0));
        for (i = 0; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
        ctx.lineTo(pts[pts.length - 1][0], Y(0));
        ctx.closePath();
        ctx.fillStyle = g; ctx.fill();
        ctx.restore();

        ctx.save();
        ctx.beginPath();
        for (i = 0; i < pts.length; i++) (i ? ctx.lineTo : ctx.moveTo).call(ctx, pts[i][0], pts[i][1]);
        ctx.strokeStyle = P.acc; ctx.lineWidth = 2.5;
        ctx.lineJoin = 'round'; ctx.lineCap = 'round';
        ctx.stroke();
        ctx.restore();

        for (i = 1; i <= 5; i++) {
            var lv = levelAt(i * k);
            ctx.save();
            ctx.fillStyle = P.acc;
            ctx.beginPath(); ctx.arc(X(i / 5), Y(lv), 3, 0, 6.2832); ctx.fill();
            ctx.restore();
        }

        if (S.px != null && span > 0) {
            var fr = Math.min(1, Math.max(0, S.px / span));
            var lvl = levelAt(S.px);
            line(X(fr), GEO.padT, X(fr), Y(0), P.warn, 1.5, [4, 3]);
            ctx.save();
            ctx.fillStyle = P.warn;
            ctx.beginPath(); ctx.arc(X(fr), Y(lvl), 4.5, 0, 6.2832); ctx.fill();
            ctx.restore();
            chip(nfd(lvl * 100, 1) + '% · ' + fmtV(lvl * S.V), X(fr), Y(lvl), P.panel, P.ink);
        }
    }

    /* ── АЧХ и фаза ─────────────────────────────────────────────────────── */
    function drawBode(P, isPhase) {
        var c = fc();
        if (!(c > 0)) return;
        var lo = Math.log10(c) - 3, hi = Math.log10(c) + 3, i, d;
        var fx = function (f) { return X((Math.log10(f) - lo) / (hi - lo)); };

        /* Подпись каждой декады на узком холсте не помещается: семь отметок на
           328 px телефона дают 47 px на «100 кГц». Сетка остаётся полной —
           редеют только подписи, потому что мельчить шрифт нельзя. */
        var every = GEO.w < 420 ? 2 : 1, k = 0;
        for (d = Math.ceil(lo); d <= Math.floor(hi); d++, k++) {
            var f = Math.pow(10, d);
            line(fx(f), GEO.padT, fx(f), Y(0), P.grid, 1, [3, 4]);
            if (k % every === 0) label(fmtF(f, 1), fx(f), Y(0) + 12, P.txt, 'center', 11);
        }

        var yv, ys;
        if (isPhase) {
            ys = [90, 45, 0, -45, -90];
            yv = function (v) { return (v + 96) / 192; };
        } else {
            /* Верх шкалы поднят до +6 дБ намеренно: при нуле полка ФВЧ ложится
               на самую кромку поля, и подпись «0» обрезается рамкой. */
            ys = [0, -3, -20, -40, -60];
            yv = function (v) { return (v + 64) / 70; };
        }
        /* ⚠️ 0 дБ и −3 дБ на шкале 70 дБ расходятся на 7 px, а шрифт подписи —
           11 px: обе подписи рисовались друг на друге и читались как «−9».
           Линию 0 дБ оставляем (она — потолок полки), подпись снимаем: важна
           здесь именно −3 дБ, и она же названа в ответе над графиком. */
        for (i = 0; i < ys.length; i++) {
            var yy = Y(yv(ys[i])), key = ys[i] === -3 || ys[i] === 0;
            line(GEO.padL, yy, GEO.w - GEO.padR, yy, key ? P.gridHi : P.grid, 1, [2, 5]);
            if (!isPhase && ys[i] === 0) continue;
            label(ys[i] + (isPhase ? '°' : ''), GEO.padL - 6, yy, P.txt, 'end');
        }

        /* Точка среза — опорная линия поля, а не подвижный маркер, поэтому она
           приглушена и подписана у оси. Раньше она рисовалась тем же цветом и
           тем же пунктиром, что курсор, и в момент открытия вкладки они
           совпадали: на экране была одна линия с двумя подписями друг на друге. */
        line(fx(c), GEO.padT, fx(c), Y(0), P.gridHi, 1.5, [5, 4]);
        label('fc ' + fmtF(c, 3), fx(c) + 5, Y(0) - 9, P.txt2, 'start', 11);

        /* Кривые подписаны СВОИМИ концами, а не легендой в углу: легенда
           заставляет искать соответствие, а на правом краю ФНЧ и ФВЧ и так
           расходятся на всю высоту поля. */
        var N = 220, ends = [];
        [false, true].forEach(function (hp) {
            ctx.save();
            ctx.beginPath();
            var lastY = 0;
            for (i = 0; i <= N; i++) {
                var f = Math.pow(10, lo + (hi - lo) * i / N);
                var v = isPhase ? phaseDeg(f, hp) : gainDb(f, hp);
                lastY = Y(Math.min(1.02, Math.max(-.02, yv(v))));
                (i ? ctx.lineTo : ctx.moveTo).call(ctx, fx(f), lastY);
            }
            ctx.strokeStyle = hp ? P.txt2 : P.acc;
            ctx.lineWidth = hp ? 1.8 : 2.5;
            if (hp) ctx.setLineDash([6, 4]);
            ctx.lineJoin = 'round';
            ctx.stroke();
            ctx.restore();
            ends.push({ hp: hp, y: lastY });
        });
        ends.forEach(function (e) {
            // подпись уводится от самой линии в ту сторону, где есть место
            var up = e.y > (GEO.padT + Y(0)) / 2;
            label(e.hp ? (L.hp || 'HP') : (L.lp || 'LP'),
                  GEO.w - GEO.padR - 4, e.y + (up ? -11 : 12),
                  e.hp ? P.txt2 : P.acc, 'end', 11);
        });

        if (S.px != null && S.px > 0) {
            var f2 = Math.min(Math.pow(10, hi), Math.max(Math.pow(10, lo), S.px));
            var v2 = isPhase ? phaseDeg(f2, false) : gainDb(f2, false);
            var yy2 = Y(Math.min(1.02, Math.max(-.02, yv(v2))));
            line(fx(f2), GEO.padT, fx(f2), Y(0), P.warn, 1.5, [4, 3]);
            ctx.save();
            ctx.fillStyle = P.warn;
            ctx.beginPath(); ctx.arc(fx(f2), yy2, 4.5, 0, 6.2832); ctx.fill();
            ctx.restore();
            chip(isPhase ? fmtDeg(v2) : fmtDb(v2),
                 fx(f2), yy2, P.panel, P.ink);
        }
    }

    /** Цвет темы приходит строкой любого вида — превращаем в rgba честно. */
    function hexA(col, a) {
        col = String(col).trim();
        var m = col.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
        if (m) {
            var h = m[1];
            if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
            return 'rgba(' + parseInt(h.slice(0, 2), 16) + ',' + parseInt(h.slice(2, 4), 16) + ','
                + parseInt(h.slice(4, 6), 16) + ',' + a + ')';
        }
        m = col.match(/^rgba?\(([^)]+)\)$/i);
        if (m) {
            var p = m[1].split(/[,\s/]+/).filter(Boolean);
            return 'rgba(' + p[0] + ',' + p[1] + ',' + p[2] + ',' + a + ')';
        }
        return 'rgba(56,189,248,' + a + ')';
    }

    /* ═══════════════════════════════════════════════════════════════════════
       8. ВЫВОД
       ═══════════════════════════════════════════════════════════════════════ */

    /* ⚠️ Единицы в <select> выбираются ЧИСЛОМ, а не строкой. Блейд пишет
       value="1e-6", а String(1e-6) в JS — "0.000001": сравнение строк не
       совпадает никогда, `select.value = …` тихо не находит вариант, и
       selectedIndex становится −1. Выглядит это как пустой селектор единицы
       после подстановки номинала — без единой ошибки в консоли. */
    function fill(sel, opts, cur) {
        var s = $(sel), html = '', i;
        for (i = 0; i < opts.length; i++) {
            html += '<option value="' + opts[i][0] + '">' + opts[i][1] + '</option>';
        }
        s.innerHTML = html;
        if (cur != null) setUnit(sel, cur);
    }
    /** Выбирает вариант, чьё числовое значение ближе всего к factor. */
    function setUnit(sel, factor) {
        var s = $(sel), best = -1, bestErr = Infinity, i, v;
        for (i = 0; i < s.options.length; i++) {
            v = parseFloat(s.options[i].value);
            if (!isFinite(v) || v <= 0) continue;
            var err = Math.abs(Math.log(v / factor));
            if (err < bestErr) { bestErr = err; best = i; }
        }
        if (best >= 0) s.selectedIndex = best;
    }

    function ladderOpts(kind) {
        return LADDER[kind].map(function (p) { return [String(p[0]), p[1]]; });
    }
    /** Единица, в которой значение читается без нулей. */
    function bestUnit(v, kind) {
        var l = LADDER[kind], e = 1 - 1e-9, i;
        for (i = 0; i < l.length; i++) if (v >= l[i][0] * e) return l[i][0];
        return l[l.length - 1][0];
    }
    /** Число В ПОЛЕ: разделитель локали, 4 значащих. Парсер понимает запятую,
        поэтому поле остаётся редактируемым. */
    function toField(v) {
        if (!isFinite(v)) return '';
        try {
            return new Intl.NumberFormat(LOC, { maximumSignificantDigits: 4, useGrouping: false }).format(v);
        } catch (_) { return String(+v.toPrecision(4)); }
    }

    function renderAnswers() {
        var k = tau(), c = fc();
        $('rccTau').textContent = k > 0 ? fmtT(k) : '—';
        $('rccFc').textContent = c > 0 ? fmtF(c) : '—';
        $('rccFull').textContent = k > 0 ? fmtT(5 * k) : '—';
        $('rccHalf').textContent = k > 0 ? fmtT(k * Math.LN2) : '—';
        $('rccEnergy').textContent = (S.C > 0 && S.V > 0) ? fmtJ(.5 * S.C * S.V * S.V) : '—';
        $('rccIpeak').textContent = (S.R > 0 && S.V > 0) ? fmtA(S.V / S.R) : '—';

        // допуск
        var rb = tolBounds(S.tolR), cb = tolBounds(S.tolC);
        var el = $('rccSpread');
        if ((rb[0] || rb[1] || cb[0] || cb[1]) && k > 0) {
            var tmin = S.R * (1 + rb[0]) * S.C * (1 + cb[0]);
            var tmax = S.R * (1 + rb[1]) * S.C * (1 + cb[1]);
            el.hidden = false;
            el.className = 'tc-msg';
            el.textContent = (L.spread || '')
                .replace(':rt', labelOf('rccTolR')).replace(':ct', labelOf('rccTolC'))
                .replace(':tmin', fmtT(tmin)).replace(':tmax', fmtT(tmax))
                .replace(':fmin', fmtF(1 / (2 * Math.PI * tmax)))
                .replace(':fmax', fmtF(1 / (2 * Math.PI * tmin)));
            $('rccTolSub').textContent = fmtT(tmin) + ' … ' + fmtT(tmax);
        } else {
            el.hidden = true;
            $('rccTolSub').textContent = L.spread_none || '';
        }
    }
    function labelOf(id) {
        var s = $(id);
        return s.options[s.selectedIndex] ? s.options[s.selectedIndex].textContent : '';
    }

    function renderTable() {
        var head = $('rccTblHead'), body = $('rccRows'), html = '', i;
        var k = tau();
        if (S.view === 'curve') {
            $('rccTblTitle').textContent = L.tbl_curve || '';
            $('rccTblSub').textContent = L.sub_curve || '';
            head.innerHTML = '<tr><th>τ</th><th>' + esc(TH.time) + '</th><th>' + esc(TH.charge)
                + '</th><th>' + esc(TH.discharge) + '</th><th>' + esc(TH.volt) + '</th></tr>';
            var pct = [[1, 63.2, 36.8], [2, 86.5, 13.5], [3, 95.0, 5.0], [4, 98.2, 1.8], [5, 99.3, 0.7]];
            for (i = 0; i < pct.length; i++) {
                var lvl = (S.mode === 'charge' ? pct[i][1] : pct[i][2]) / 100;
                html += '<tr><td>' + pct[i][0] + 'τ</td><td class="num">' + esc(fmtT(pct[i][0] * k))
                    + '</td><td class="num">' + nfd(pct[i][1], 1) + '%</td><td class="num">'
                    + nfd(pct[i][2], 1) + '%</td><td class="num">' + esc(fmtV(lvl * S.V)) + '</td></tr>';
            }
        } else {
            $('rccTblTitle').textContent = L.tbl_bode || '';
            $('rccTblSub').textContent = L.sub_bode || '';
            head.innerHTML = '<tr><th>' + esc(TH.freq) + '</th><th>' + esc(TH.gainLp)
                + '</th><th>' + esc(TH.ratio) + '</th><th>' + esc(TH.gainHp)
                + '</th><th>' + esc(TH.phase) + '</th></tr>';
            var c = fc();
            var mult = [0.1, 0.5, 1, 2, 10, 100];
            for (i = 0; i < mult.length; i++) {
                var f = c * mult[i], db = gainDb(f, false);
                html += '<tr' + (mult[i] === 1 ? ' class="now"' : '') + '><td class="num">'
                    + esc(fmtF(f)) + '</td><td class="num">' + esc(fmtDb(db))
                    + '</td><td class="num">' + nfd(Math.pow(10, db / 20) * 100, 1) + '%'
                    + '</td><td class="num">' + esc(fmtDb(gainDb(f, true)))
                    + '</td><td class="num">' + esc(fmtDeg(phaseDeg(f, false))) + '</td></tr>';
            }
        }
        body.innerHTML = html;
    }

    function esc(s) {
        return String(s).replace(/[&<>"]/g, function (c) {
            return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
        });
    }

    /* ── строка точки ──────────────────────────────────────────────────── */
    var pxLock = false;
    function renderPoint(skipInputs) {
        var k = tau();
        if (S.view === 'curve') {
            if (S.px == null && k > 0) S.px = k;
            var t = S.px || 0, lvl = levelAt(t);
            $('rccPxL').textContent = TH.pTime;
            $('rccPyL').textContent = TH.pVolt;
            if (!skipInputs) {
                setPair('rccPx', 'rccPxU', t, 'time');
                setPair('rccPy', 'rccPyU', lvl * S.V, 'volt');
            }
            $('rccRead').innerHTML = (L.point_curve || '')
                .replace(':t', '<b>' + esc(fmtT(t)) + '</b>')
                .replace(':pct', '<b>' + nfd(lvl * 100, 1) + '%</b>')
                .replace(':u', '<b>' + esc(fmtV(lvl * S.V)) + '</b>')
                .replace(':i', '<b>' + esc(fmtA(currentAt(t))) + '</b>');
        } else {
            var c = fc();
            if (S.px == null && c > 0) S.px = c;
            var f = S.px || 0;
            var isPhase = S.view === 'phase';
            $('rccPxL').textContent = TH.pFreq;
            $('rccPyL').textContent = isPhase ? TH.pPhase : TH.pGain;
            if (!skipInputs) {
                setPair('rccPx', 'rccPxU', f, 'freq');
                var yEl = $('rccPy'), yU = $('rccPyU');
                yU.innerHTML = '<option value="1">' + esc(isPhase ? U.deg : U.db) + '</option>';
                yEl.value = nfd(isPhase ? phaseDeg(f, false) : gainDb(f, false), 1);
            }
            $('rccRead').innerHTML = (L.point_bode || '')
                .replace(':f', '<b>' + esc(fmtF(f)) + '</b>')
                .replace(':lp', '<b>' + esc(fmtDb(gainDb(f, false))) + '</b>')
                .replace(':hp', '<b>' + esc(fmtDb(gainDb(f, true))) + '</b>')
                .replace(':ph', '<b>' + esc(fmtDeg(phaseDeg(f, false))) + '</b>');
        }
    }
    function setPair(inId, unId, value, kind) {
        var f = bestUnit(value, kind);
        fill(unId, ladderOpts(kind), f);
        $(inId).value = value > 0 ? toField(value / f) : '0';
    }

    /* ── подбор ───────────────────────────────────────────────────────── */
    function renderFits() {
        var host = $('rccFits');
        var p = parseValue($('rccGoal').value, {});
        var raw = p ? p.n : parseFloat(String($('rccGoal').value).replace(',', '.'));
        var unit = parseFloat($('rccGoalU').value) || 1;
        var goal = (isFinite(raw) ? raw : 0) * unit;
        var targetTau = S.target === 'tau' ? goal : (goal > 0 ? 1 / (2 * Math.PI * goal) : 0);
        var fits = findFits(targetTau, S.series);
        if (!fits.length) {
            host.innerHTML = '<p class="tc-note">' + esc(L.no_fit || '') + '</p>';
            return;
        }
        var html = '', i;
        for (i = 0; i < fits.length; i++) {
            var f = fits[i];
            var dev = f.dev * 100;
            var devTxt = (L.dev || ':d%').replace(':d', (dev >= 0 ? '+' : '') + nfd(dev, 1));
            html += '<button type="button" class="rcc-fit" data-r="' + f.r + '" data-c="' + f.c + '">'
                + '<span class="rcc-fit-v">' + esc(fmtR(f.r)) + ' + ' + esc(fmtC(f.c)) + '</span>'
                + '<span class="rcc-fit-d' + (Math.abs(dev) < 2 ? ' ok' : '') + '">'
                + esc(S.target === 'tau' ? fmtT(f.tau, 3) : fmtF(1 / (2 * Math.PI * f.tau), 3))
                + ' · ' + esc(devTxt) + '</span></button>';
        }
        host.innerHTML = html;
    }

    /* ═══════════════════════════════════════════════════════════════════════
       9. СВЯЗЬ С ПОЛЯМИ
       ═══════════════════════════════════════════════════════════════════════ */

    /* Подписи, меняющиеся в рантайме. Заполняются один раз при старте из
       разметки и data-атрибутов — см. блок «СТАРТ» внизу файла. */
    var TH = {};

    /* Подсказка «понято как 4,7 мкФ» гаснет по таймеру, а не на следующем
       чтении поля: мы САМИ переписали «4u7» в «4,7», поэтому уже второй разбор
       видит обычное число — и подсказка исчезала раньше, чем её успевали
       прочитать. Проверено: `fill()` в браузере шлёт input и change подряд. */
    var tipTimers = {};
    function showTip(tipId, text) {
        var tip = $(tipId);
        if (!tip) return;
        clearTimeout(tipTimers[tipId]);
        tip.textContent = text;
        tip.className = text ? 'rcc-tip hit' : 'rcc-tip';
        if (text) {
            tipTimers[tipId] = setTimeout(function () {
                tip.textContent = ''; tip.className = 'rcc-tip';
            }, 5000);
        }
    }

    /** Разбор пары «число + единица» с поддержкой сокращённой записи. */
    function readPair(inId, unId, sufMap, tipId) {
        var el = $(inId), sel = $(unId);
        var kind = unId === 'rccRu' ? 'ohm' : 'farad';
        var p = parseValue(el.value, sufMap);
        if (!p) return NaN;
        if (p.factor != null) {
            // «4u7» — буква стоит на месте запятой: чиним и число, и селектор
            var total = p.n * p.factor;
            var f = bestUnit(total, kind);
            setUnit(unId, f);
            el.value = toField(total / f);
            showTip(tipId, (L.shorthand || '').replace(':v', kind === 'ohm' ? fmtR(total) : fmtC(total)));
            return total;
        }
        return p.n * (parseFloat(sel.value) || 1);
    }

    function readAll() {
        var r = readPair('rccR', 'rccRu', SUF_R, 'rccRtip');
        var c = readPair('rccC', 'rccCu', SUF_C, 'rccCtip');
        var v = parseValue($('rccV').value, {});
        if (isFinite(r) && r >= 0) S.R = r;
        if (isFinite(c) && c >= 0) S.C = c;
        if (v && isFinite(v.n) && v.n >= 0) S.V = v.n;
    }

    var permaTimer = null;
    function permalink() {
        clearTimeout(permaTimer);
        permaTimer = setTimeout(function () {
            try {
                var q = new URLSearchParams();
                q.set('r', toShort(S.R, 'ohm'));
                q.set('c', toShort(S.C, 'farad'));
                q.set('v', String(+S.V.toPrecision(6)));
                if (S.mode !== 'charge') q.set('m', S.mode);
                history.replaceState(null, '', location.pathname + '?' + q.toString());
            } catch (_) {}
        }, 400);
    }

    function restoreFromUrl() {
        var q;
        try { q = new URLSearchParams(location.search); } catch (_) { return; }
        var r = q.get('r'), c = q.get('c'), v = q.get('v'), m = q.get('m');
        if (r) { var pr = parseValue(r, SUF_R); if (pr) applyPair('rccR', 'rccRu', pr.factor != null ? pr.n * pr.factor : pr.n, 'ohm'); }
        if (c) { var pc = parseValue(c, SUF_C); if (pc) applyPair('rccC', 'rccCu', pc.factor != null ? pc.n * pc.factor : pc.n, 'farad'); }
        if (v && isFinite(parseFloat(v))) $('rccV').value = String(parseFloat(v));
        if (m === 'discharge') setMode('discharge');
    }

    function applyPair(inId, unId, total, kind) {
        if (!(total > 0)) return;
        var f = bestUnit(total, kind);
        setUnit(unId, f);
        $(inId).value = toField(total / f);
    }

    function setMode(mode) {
        S.mode = mode;
        segOn('rccMode', 'mode', mode);
        $('rccFullL').textContent = mode === 'charge' ? TH.full : TH.fullD;
        $('rccTauNote').textContent = mode === 'charge' ? TH.tauNote : TH.tauNoteD;
    }
    function segOn(wrapId, attr, val) {
        var bs = $(wrapId).querySelectorAll('.tc-seg-b'), i;
        for (i = 0; i < bs.length; i++) {
            var on = bs[i].getAttribute('data-' + attr) === val;
            bs[i].classList.toggle('on', on);
            bs[i].setAttribute('aria-pressed', on ? 'true' : 'false');
        }
    }

    function describeChart() {
        var k = tau(), c = fc();
        if (S.view === 'curve') {
            cv.setAttribute('aria-label', (L.alt_curve || '')
                .replace(':mode', S.mode === 'charge' ? (L.charge || '') : (L.discharge || ''))
                .replace(':tau', fmtT(k)).replace(':full', fmtT(5 * k)));
        } else {
            cv.setAttribute('aria-label', (S.view === 'phase' ? (L.alt_phase || '') : (L.alt_bode || ''))
                .replace(':fc', fmtF(c)));
        }
    }

    var rafId = 0;
    function render(skipPointInputs) {
        renderAnswers();
        renderPoint(skipPointInputs);
        renderTable();
        describeChart();
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(function () { rafId = 0; draw(); });
        permalink();
    }

    /* Маркер держится ОТНОСИТЕЛЬНОГО места, а не абсолютного.
       Человек поставил его на 1τ, поменял ёмкость — и ждёт, что смотрит
       по-прежнему на 1τ. При абсолютной привязке 5,17 мкс на цепи с τ = 1 с
       превращаются в «0,0% от питания»: формально верно, практически мусор. */
    function recalc(skipPointInputs) {
        var oldTau = tau(), oldFc = fc(), rel = null;
        if (S.px != null) {
            if (S.view === 'curve' && oldTau > 0) rel = S.px / oldTau;
            else if (S.view !== 'curve' && oldFc > 0) rel = S.px / oldFc;
        }
        readAll();
        if (rel != null && isFinite(rel)) {
            S.px = (S.view === 'curve' ? tau() : fc()) * rel;
        }
        render(skipPointInputs);
    }

    /* ═══════════════════════════════════════════════════════════════════════
       10. СОБЫТИЯ
       ═══════════════════════════════════════════════════════════════════════ */

    ['rccR', 'rccC', 'rccV'].forEach(function (id) {
        $(id).addEventListener('input', function () { recalc(); });
        $(id).addEventListener('change', function () { recalc(); });
        // стрелками — по ряду номиналов, а не «плюс один»: у резистора
        // следующее значение это 11 кОм, а не 10 001 Ом
        $(id).addEventListener('keydown', function (e) {
            if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
            if (id === 'rccV') return;
            e.preventDefault();
            var kind = id === 'rccR' ? 'ohm' : 'farad';
            var mant = id === 'rccR' ? (SERIES[S.series] || E24) : E6;
            var cur = id === 'rccR' ? S.R : S.C;
            var next = stepSeries(cur, e.key === 'ArrowUp' ? 1 : -1, mant);
            applyPair(id, id + 'u', next, kind);
            recalc();
        });
    });
    ['rccRu', 'rccCu'].forEach(function (id) {
        $(id).addEventListener('change', function () { recalc(); });
    });

    $('rccMode').addEventListener('click', function (e) {
        var b = e.target.closest('.tc-seg-b'); if (!b) return;
        setMode(b.getAttribute('data-mode'));
        kitFeature('mode', { to: S.mode });
        render();
    });

    $('rccView').addEventListener('click', function (e) {
        var b = e.target.closest('.tc-seg-b'); if (!b) return;
        var v = b.getAttribute('data-view');
        if (v === S.view) return;
        var apply = function () {
            S.view = v;
            S.px = null;                     // маркер по умолчанию садится на τ / fc
            segOn('rccView', 'view', v);
            render();
        };
        if (window.__viewTransition) window.__viewTransition(apply); else apply();
        kitFeature('view', { to: v });
    });

    $('rccPresets').addEventListener('click', function (e) {
        var b = e.target.closest('.tc-chip'); if (!b) return;
        applyPair('rccR', 'rccRu', parseFloat(b.getAttribute('data-r')), 'ohm');
        applyPair('rccC', 'rccCu', parseFloat(b.getAttribute('data-c')), 'farad');
        $('rccV').value = b.getAttribute('data-v');
        S.px = null;
        kitFeature('preset', { name: b.textContent.trim().slice(0, 40) });
        recalc();
    });

    // строка точки — двусторонняя
    ['rccPx', 'rccPxU'].forEach(function (id) {
        $(id).addEventListener('input', onPointX);
        $(id).addEventListener('change', onPointX);
    });
    ['rccPy', 'rccPyU'].forEach(function (id) {
        $(id).addEventListener('input', onPointY);
        $(id).addEventListener('change', onPointY);
    });
    function onPointX() {
        if (pxLock) return;
        var n = parseFloat(String($('rccPx').value).replace(',', '.'));
        var f = parseFloat($('rccPxU').value) || 1;
        if (!isFinite(n) || n < 0) return;
        S.px = n * f;
        pxLock = true; render(true); pxLock = false;
        syncPointY();
    }
    function onPointY() {
        if (pxLock) return;
        var n = parseFloat(String($('rccPy').value).replace(',', '.'));
        if (!isFinite(n)) return;
        if (S.view === 'curve') {
            var f = parseFloat($('rccPyU').value) || 1;
            var volts = n * f;
            if (!(S.V > 0)) return;
            var t = timeAtLevel(Math.min(.999999, Math.max(0, volts / S.V)));
            if (!isFinite(t)) t = 5 * tau();
            S.px = Math.min(t, 20 * tau());
        } else {
            S.px = S.view === 'phase' ? freqAtPhase(n) : freqAtDb(n);
        }
        pxLock = true; render(true); pxLock = false;
        syncPointX();
    }
    /** После правки одного поля второе обязано показать пересчитанное. */
    function syncPointY() {
        pxLock = true;
        if (S.view === 'curve') setPair('rccPy', 'rccPyU', levelAt(S.px) * S.V, 'volt');
        else $('rccPy').value = nfd(S.view === 'phase' ? phaseDeg(S.px, false) : gainDb(S.px, false), 1);
        pxLock = false;
    }
    function syncPointX() {
        pxLock = true;
        setPair('rccPx', 'rccPxU', S.px, S.view === 'curve' ? 'time' : 'freq');
        pxLock = false;
    }

    // маркер мышью и пальцем
    function pointerToDomain(ev) {
        var r = cv.getBoundingClientRect();
        var fr = (ev.clientX - r.left - GEO.padL) / Math.max(1, (r.width - GEO.padL - GEO.padR));
        fr = Math.min(1, Math.max(0, fr));
        if (S.view === 'curve') return fr * 5 * tau();
        var c = fc();
        if (!(c > 0)) return 0;
        var lo = Math.log10(c) - 3, hi = Math.log10(c) + 3;
        return Math.pow(10, lo + fr * (hi - lo));
    }
    var dragging = false;
    cv.addEventListener('pointerdown', function (e) {
        dragging = true;
        try { cv.setPointerCapture(e.pointerId); } catch (_) {}
        S.px = pointerToDomain(e); render();
    });
    cv.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        e.preventDefault();
        S.px = pointerToDomain(e); render();
    });
    ['pointerup', 'pointercancel'].forEach(function (t) {
        cv.addEventListener(t, function () { dragging = false; });
    });
    // с клавиатуры холст двигает маркер шагами — то же действие без указателя
    cv.addEventListener('keydown', function (e) {
        var step = e.shiftKey ? .1 : .02, d = 0;
        if (e.key === 'ArrowRight') d = 1; else if (e.key === 'ArrowLeft') d = -1; else return;
        e.preventDefault();
        if (S.view === 'curve') {
            var span = 5 * tau();
            S.px = Math.min(span, Math.max(0, (S.px || 0) + d * step * span));
        } else {
            S.px = Math.max(1e-9, (S.px || fc()) * Math.pow(10, d * step * 6));
        }
        render();
    });

    // подбор
    $('rccTarget').addEventListener('click', function (e) {
        var b = e.target.closest('.tc-seg-b'); if (!b) return;
        S.target = b.getAttribute('data-target');
        segOn('rccTarget', 'target', S.target);
        $('rccGoalLab').textContent = S.target === 'tau' ? TH.goalTau : TH.goalFc;
        fill('rccGoalU', ladderOpts(S.target === 'tau' ? 'time' : 'freq'),
             S.target === 'tau' ? '0.001' : '1');
        $('rccGoal').value = S.target === 'tau' ? '10' : '1';
        renderFits();
    });
    ['rccGoal', 'rccGoalU', 'rccSeries'].forEach(function (id) {
        $(id).addEventListener('input', function () { S.series = $('rccSeries').value; renderFits(); });
        $(id).addEventListener('change', function () { S.series = $('rccSeries').value; renderFits(); });
    });
    $('rccFits').addEventListener('click', function (e) {
        var b = e.target.closest('.rcc-fit'); if (!b) return;
        applyPair('rccR', 'rccRu', parseFloat(b.getAttribute('data-r')), 'ohm');
        applyPair('rccC', 'rccCu', parseFloat(b.getAttribute('data-c')), 'farad');
        S.px = null;
        kitFeature('fit_applied', {});
        recalc();
    });

    // допуск
    ['rccTolR', 'rccTolC'].forEach(function (id) {
        $(id).addEventListener('change', function () {
            S.tolR = $('rccTolR').value; S.tolC = $('rccTolC').value;
            renderAnswers();
        });
    });

    /* Тема меняется без перезагрузки — холст рисуется цветами темы, значит
       обязан перерисоваться. Иначе светлая страница получает тёмную сетку. */
    if (window.MutationObserver) {
        new MutationObserver(function () { draw(); })
            .observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    }

    /* Пропорциональный перенос: холст следит за СВОИМ контейнером, а не за
       окном — в узкой колонке рядом с сайдбаром ширина другая. */
    if (window.ResizeObserver) {
        new ResizeObserver(function () { draw(); }).observe(cv);
    } else {
        window.addEventListener('resize', function () { draw(); });
    }

    /* На печать ящики раскрываются: лист с одним заголовком «Таблица по шагам τ»
       и пустотой под ним — не документ. */
    /* ⚠️ Открытие должно быть ИДЕМПОТЕНТНЫМ. Печать объявляет о себе двумя
       независимыми путями — событием `beforeprint` и сменой медиа `print`, — и
       браузер может прислать оба. Без флага второй вызов запоминал бы уже
       раскрытое состояние, и после печати ящики оставались открытыми навсегда:
       человек закрыл справочник, распечатал лист и получил его обратно. */
    var wasOpen = null;
    function openAll() {
        if (wasOpen) return;
        wasOpen = [];
        app.querySelectorAll('details').forEach(function (d, i) { wasOpen[i] = d.open; d.open = true; });
        draw();
    }
    function restoreAll() {
        if (!wasOpen) return;
        var snap = wasOpen;
        wasOpen = null;
        app.querySelectorAll('details').forEach(function (d, i) { d.open = !!snap[i]; });
    }
    window.addEventListener('beforeprint', openAll);
    window.addEventListener('afterprint', restoreAll);
    if (window.matchMedia) {
        var mq = window.matchMedia('print');
        if (mq.addEventListener) {
            mq.addEventListener('change', function (e) { e.matches ? openAll() : restoreAll(); });
        }
    }

    function kitFeature(name, data) {
        try { if (window.__calcKit) window.__calcKit.feature(name, data); } catch (_) {}
    }

    /* ═══════════════════════════════════════════════════════════════════════
       11. СТАРТ
       ═══════════════════════════════════════════════════════════════════════ */

    // подписи, которые меняются в рантайме, забираем из разметки ОДИН раз
    TH = {
        full: $('rccFullL').textContent,
        fullD: app.getAttribute('data-full-d') || $('rccFullL').textContent,
        tauNote: $('rccTauNote').textContent,
        tauNoteD: app.getAttribute('data-tau-note-d') || $('rccTauNote').textContent,
        goalTau: $('rccGoalLab').textContent,
        goalFc: app.getAttribute('data-goal-fc') || $('rccGoalLab').textContent,
        pTime: $('rccPxL').textContent,
        pVolt: $('rccPyL').textContent,
        pFreq: app.getAttribute('data-p-freq') || 'f',
        pGain: app.getAttribute('data-p-gain') || 'dB',
        pPhase: app.getAttribute('data-p-phase') || '°',
        time: app.getAttribute('data-c-time') || 't',
        charge: app.getAttribute('data-c-charge') || '%',
        discharge: app.getAttribute('data-c-discharge') || '%',
        volt: app.getAttribute('data-c-volt') || 'U',
        freq: app.getAttribute('data-c-freq') || 'f',
        gainLp: app.getAttribute('data-c-lp') || 'LP',
        gainHp: app.getAttribute('data-c-hp') || 'HP',
        ratio: app.getAttribute('data-c-ratio') || '%',
        phase: app.getAttribute('data-c-phase') || '°'
    };

    fill('rccGoalU', ladderOpts('time'), '0.001');
    restoreFromUrl();
    readAll();
    setMode(S.mode);
    render();
    renderFits();

    try {
        if (window.__calcKit) {
            window.__calcKit.init({
                root: '#rccApp',
                answer: function () { return $('rccTau').textContent + ' / ' + $('rccFc').textContent; },
                meta: function () { return { mode: S.mode, view: S.view, series: S.series }; },
                share: function () {
                    return (L.share || '')
                        .replace(':r', fmtR(S.R)).replace(':c', fmtC(S.C))
                        .replace(':tau', fmtT(tau())).replace(':fc', fmtF(fc()));
                },
                shareInto: '#rccShareHost'
            });
        }
    } catch (_) {}
})();
